'use client';

import {
  isShopPath,
  setShopFocusIntent,
  SHOP_FOCUS_FILTER_BAR,
  SHOP_FOCUS_RESULTS,
} from '@/features/shop/shopFocusIntent';
import NextForm from 'next/form';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState, useTransition } from 'react';
import {
  Form as BootstrapForm,
  Button,
  Form,
  InputGroup,
} from 'react-bootstrap';
import { useFormStatus } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import './searchInput.scss';

const STORAGE_KEY = 'search:lastKeyword';

function buildHref(pathname, params) {
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

function fillTemplate(template, replacements = {}) {
  return Object.entries(replacements).reduce(
    (message, [key, value]) =>
      message.replaceAll(`{{${key}}}`, String(value ?? '')),
    String(template || ''),
  );
}

function SearchSubmitButton({
  short = false,
  squared = false,
  label = 'Search',
}) {
  const { pending } = useFormStatus();

  return (
    <Button
      type="submit"
      variant="secondary"
      className={`search-input-btn d-flex align-items-center py-1 px-3 ${
        squared ? 'rounded-0' : ''
      }`}
      aria-label={label}
      disabled={pending}
    >
      <span className={`fs-4 mt-2 ${short ? 'px-2' : ''}`}>🔍</span>
      {!short ? <span className="fs-6">{label}</span> : null}
    </Button>
  );
}

export default function SearchInput({
  initialKeyword = '',
  searchItem = '',
  short = false,
  onClear,
  onSubmit,
  placeholder = 'Search products...',
  className = '',
  targetPathname = '',
  squared = false,
  searchButtonLabel = 'Search',
  clearLabel = 'Clear',
  searchStatusTemplate = 'Searching for {{keyword}}.',
  clearedStatusMessage = 'Search cleared.',
}) {
  const pathname = targetPathname || usePathname() || '/shop';
  const router = useRouter();
  const searchParams = useSearchParams();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const restoredFromStorageRef = useRef(false);
  const [isPending, startTransition] = useTransition();
  const [isMobileCompact, setIsMobileCompact] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialKeyword);
  const [liveMessage, setLiveMessage] = useState('');
  const isCompact = short || isMobileCompact;
  const preservedParams = Array.from(searchParams.entries()).filter(
    ([key, value]) =>
      typeof value === 'string' &&
      value.trim() &&
      key !== 'keyword' &&
      key !== 'page' &&
      key !== 'q',
  );

  useEffect(() => {
    const updateCompactMode = () => {
      setIsMobileCompact(window.innerWidth < 576);
    };

    updateCompactMode();
    window.addEventListener('resize', updateCompactMode);
    window.addEventListener('orientationchange', updateCompactMode);

    return () => {
      window.removeEventListener('resize', updateCompactMode);
      window.removeEventListener('orientationchange', updateCompactMode);
    };
  }, []);

  useEffect(() => {
    if (initialKeyword) {
      setSearchTerm(initialKeyword);
      return;
    }

    if (!restoredFromStorageRef.current) {
      restoredFromStorageRef.current = true;

      try {
        const storedKeyword = window.localStorage.getItem(STORAGE_KEY);
        if (storedKeyword) {
          setSearchTerm(storedKeyword);
          return;
        }
      } catch {
        // Ignore storage access failures in restricted environments.
      }
    }

    setSearchTerm('');
  }, [initialKeyword]);

  useEffect(() => {
    if (!liveMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setLiveMessage(''), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [liveMessage]);

  const navigateWithKeyword = (keyword) => {
    const params = new URLSearchParams(searchParams.toString());

    if (keyword) {
      params.set('keyword', keyword);
      params.set('page', '1');
      try {
        window.localStorage.setItem(STORAGE_KEY, keyword);
      } catch {
        // Ignore storage access failures in restricted environments.
      }
    } else {
      params.delete('keyword');
      params.delete('page');
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Ignore storage access failures in restricted environments.
      }
    }

    startTransition(() => {
      router.push(buildHref(pathname, params), { scroll: false });
    });
  };

  const handleSubmit = (event) => {
    const normalizedKeyword = searchTerm.trim();

    if (!normalizedKeyword) {
      event.preventDefault();
      handleClear();
      return;
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, normalizedKeyword);
    } catch {
      // Ignore storage access failures in restricted environments.
    }

    if (isShopPath(pathname)) {
      setShopFocusIntent(SHOP_FOCUS_RESULTS);
    }

    setLiveMessage(
      fillTemplate(searchStatusTemplate, { keyword: normalizedKeyword }),
    );
    onSubmit?.(normalizedKeyword);

    inputRef.current?.blur();
  };

  const handleClear = () => {
    setSearchTerm('');
    if (isShopPath(pathname)) {
      setShopFocusIntent(SHOP_FOCUS_FILTER_BAR);
    }

    setLiveMessage(clearedStatusMessage);
    navigateWithKeyword('');
    onClear?.();
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} role="search" className="mx-0 search-control">
      <Form
        as={NextForm}
        action={pathname}
        scroll={false}
        onSubmit={handleSubmit}
        className={`search-container ${className}`.trim()}
      >
        {preservedParams.map(([key, value], index) => (
          <input
            key={`${key}-${value}-${index}`}
            type="hidden"
            name={key}
            value={value}
          />
        ))}
        <input type="hidden" name="page" value="1" />
        <InputGroup
          className={`search-input-group ${squared ? 'rounded-0' : 'rounded-pill'} overflow-hidden shadow mx-auto flex-nowrap`}
        >
          <div className="input-control flex-grow-1 d-flex justify-content-between align-items-center gap-1">
            <BootstrapForm.Control
              ref={inputRef}
              type="text"
              name="keyword"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={placeholder || `Find ${searchItem}...`}
              spellCheck="false"
              autoComplete="off"
              className="search-input"
            />
            <Button
              variant="outline"
              onClick={handleClear}
              title={clearLabel}
              aria-label={clearLabel}
              className={`clear-search-icon border-start-0 shadow-none${
                searchTerm.trim() ? ' clear-search-icon--visible' : ''
              }`}
              aria-hidden={!searchTerm.trim()}
              disabled={isPending || !searchTerm.trim()}
            >
              <FaTimes size={20} />
            </Button>
          </div>

          <SearchSubmitButton
            short={isCompact}
            squared={squared}
            label={searchButtonLabel}
          />
        </InputGroup>
      </Form>

      <span className="visually-hidden" role="status" aria-live="polite">
        {liveMessage}
      </span>
    </div>
  );
}
